import { Outlet, useLocation, useNavigate } from "react-router-dom";
import styled from "styled-components";
import { SideBar } from "@qivia/ui";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import {
  loadCompanyAsync,
  removeToken,
  selectManager,
  removeCurrentCompany,
  removeCompany,
  selectCompany,
  getCurrentCompany,
  selectCompanyStatus,
} from "./homeSlice";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  selectVehiclesCreatedStatus,
  vehiclesSlice,
} from "./vehicles/vehiclesSlice";
import {
  driversAccessRequestsListAsync,
  driversSlice,
  selectDriverAccessRequestsList,
  selectDriverAccessRequestsListStatus,
} from "./drivers/driversSlice";
import { rulesSlice, selectRuleStatus } from "./rules/rulesSlice";
import {
  cardsSlice,
  selectCardCreationStatus,
} from "./cardsAndBadges/cards/cardsSlice";
import { stripeSlice } from "../stripe/stripeSlice";
import { sepaDebitSlice } from "./billings/sepaDebitSlice";
import {
  badgesSlice,
  selectBadgesCreationStatus,
} from "./cardsAndBadges/badges/badgesSlice";
import { AccessErrorPage } from "../../components/NoAccessErrorPage";
import { UsersSlice } from "./users/usersSlice";
import { CompanySelector } from "../../components/companySelector";
import {
  selectAllCompaniesStatus,
  selectAllActiveCompanies,
} from "./companies/companiesSlice";
import "../../i18n/i18n";
import { transactionsSlice } from "./transactions/transactionsSlice";
import { billingsSlice } from "./billings/billingsSlice";
import { notificationsSlice } from "./notifications/notificationsSlice";
import { departmentsSlice } from "./departments/departmentsSlice";
import { fleetSlice } from "./fleet/fleetSlice";
import { tripSlice } from "./fleet/trips/tripsSlice";
import { geolocationSlice } from "./geolocation/geolocationSlice";

export const Home = () => {
  const manager = useAppSelector(selectManager);
  const location = useLocation();
  const initialSelectedRoute = location.pathname.split("/")[2]
    ? location.pathname.split("/")[2]
    : "";
  const [selectedRoute, setSelectedRoute] = useState<string | undefined>(
    initialSelectedRoute,
  );

  const [fullScreen, setFullScreen] = useState<boolean>(false);

  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const company = useAppSelector(selectCompany);
  const companyStatus = useAppSelector(selectCompanyStatus);
  const loadAllCompaniesStatus = useAppSelector(selectAllCompaniesStatus);

  //RESET DES STATES DES SLICES LORSQUE LA COMPAGNIE CHANGE
  useEffect(() => {
    if (companyStatus === "success" && company) {
      void dispatch(
        sepaDebitSlice.actions.resetGetMemobankMandatePerCompanyStatus(),
      );
      void dispatch(UsersSlice.actions.resetManagersListStatus());
      void dispatch(cardsSlice.actions.resetCardsListStatus());
      void dispatch(badgesSlice.actions.resetBadgesList());
      void dispatch(rulesSlice.actions.resetRulesList());

      //VEHICLES
      void dispatch(vehiclesSlice.actions.resetListAllVehiclesInDbStatus());
      void dispatch(departmentsSlice.actions.resetDepartmentsListStatus());

      //FLEET
      void dispatch(fleetSlice.actions.resetFleetListStatus());
      void dispatch(fleetSlice.actions.resetDriversHistoryListStatus());
      void dispatch(tripSlice.actions.resetPlanningsListStatus());
      void dispatch(tripSlice.actions.resetTripsListStatus());

      //DRIVERS
      void dispatch(driversSlice.actions.resetDriversListStatus());
      void dispatch(
        driversSlice.actions.resetDriversAccessRequestsListStatus(),
      );

      //TRANSACTIONS
      void dispatch(transactionsSlice.actions.resetTransactionsListStatus());
      void dispatch(transactionsSlice.actions.resetAuthorizationsListStatus());
      void dispatch(transactionsSlice.actions.resetAsfTransactionsListStatus());

      void dispatch(transactionsSlice.actions.resetEmspCdrsListStatus());

      //BILLINGS
      void dispatch(billingsSlice.actions.resetStatementsListStatus());
      void dispatch(billingsSlice.actions.resetBankStatementsListStatus());
      void dispatch(billingsSlice.actions.resetDepositsListStatus());
      void dispatch(billingsSlice.actions.resetAsfInvoicesListStatus());
      void dispatch(billingsSlice.actions.resetInvoicesListStatus());

      //NOTIFICATIONS
      void dispatch(notificationsSlice.actions.resetNotificationsListStatus());

      //GEOLOCATION
      void dispatch(geolocationSlice.actions.resetDataPointsByVehicleStatus());
      void dispatch(
        geolocationSlice.actions.resetLastDataPointsVehiclesStatus(),
      );
    }
  }, [company, companyStatus, dispatch]);

  useEffect(() => {
    if (initialSelectedRoute !== selectedRoute) {
      setSelectedRoute(initialSelectedRoute);
    }
  }, [initialSelectedRoute, location, selectedRoute]);

  useEffect(() => {
    if (
      initialSelectedRoute === "rule" ||
      initialSelectedRoute === "rule_creation"
    ) {
      setSelectedRoute("rules");
    }
  }, [initialSelectedRoute, location, selectedRoute]);

  useEffect(() => {
    if (!company && companyStatus === "idle") {
      dispatch(getCurrentCompany());
    }
  }, [company, companyStatus, dispatch]);

  const onChangeRoute = useCallback(
    (route: string) => {
      setSelectedRoute(route);
      navigate(route);
    },
    [navigate],
  );

  const onChangeCompany = useCallback(
    (companyUuid: string) => {
      void dispatch(loadCompanyAsync({ uuid: companyUuid }));
    },
    [dispatch],
  );

  const onClickCompanyCreation = useCallback(() => {
    dispatch(stripeSlice.actions.resetStripeToken());
    dispatch(removeCompany());
    dispatch(sepaDebitSlice.actions.setConnectSepaMandateSigned(null));
    dispatch(sepaDebitSlice.actions.setPlatformSepaMandateSigned(null));
    dispatch(sepaDebitSlice.actions.setMemobankMandateSent(null));
    navigate("/new_company");
  }, [dispatch, navigate]);

  const logout = useCallback(() => {
    dispatch(removeToken());
    dispatch(removeCurrentCompany());
    navigate("/logout");
  }, [dispatch, navigate]);

  useEffect(() => {
    if (manager && manager.status !== "ACTIVE") {
      logout();
    }
  }, [logout, manager]);

  const vehiclesCreationStatus = useAppSelector(selectVehiclesCreatedStatus);
  const driversAccessRequestsList = useAppSelector(
    selectDriverAccessRequestsList,
  );
  const driversAccessRequestsListStatus = useAppSelector(
    selectDriverAccessRequestsListStatus,
  );
  const ruleCreationStatus = useAppSelector(selectRuleStatus);
  const cardCreationStatus = useAppSelector(selectCardCreationStatus);
  const badgeCreationStatus = useAppSelector(selectBadgesCreationStatus);
  const activeCompanies = useAppSelector(selectAllActiveCompanies);

  const [driverAccessRequestCreation, setDriverAccessRequestCreation] =
    useState(false);
  const [vehicleCreation, setVehicleCreation] = useState(false);
  const [ruleCreation, setRuleCreation] = useState(false);
  const [cardCreation, setCardCreation] = useState(false);
  const [badgeCreation, setBadgeCreation] = useState(false);

  useEffect(() => {
    if (
      selectedRoute &&
      company &&
      driversAccessRequestsListStatus === "idle"
    ) {
      void dispatch(driversAccessRequestsListAsync(company.uuid));
    }
  }, [dispatch, selectedRoute, company, driversAccessRequestsListStatus]);

  useEffect(() => {
    if (driversAccessRequestsListStatus !== "success") return;
    setDriverAccessRequestCreation(driversAccessRequestsList.length > 0);
  }, [driversAccessRequestsList.length, driversAccessRequestsListStatus]);

  useEffect(() => {
    if (ruleCreationStatus === "success" && selectedRoute === "") {
      setRuleCreation(true);
    }
  }, [ruleCreationStatus, selectedRoute]);

  useEffect(() => {
    if (cardCreationStatus === "success" && selectedRoute === "") {
      setCardCreation(true);
    }
  }, [cardCreationStatus, selectedRoute]);

  useEffect(() => {
    if (badgeCreationStatus === "success" && selectedRoute === "") {
      setBadgeCreation(true);
    }
  }, [badgeCreationStatus, selectedRoute]);

  useEffect(() => {
    if (vehiclesCreationStatus === "success" && selectedRoute === "") {
      setVehicleCreation(true);
    }
  }, [selectedRoute, vehiclesCreationStatus]);

  useEffect(() => {
    switch (selectedRoute) {
      case "cards":
        setCardCreation(false);
        setBadgeCreation(false);
        return;
      case "drivers":
        setDriverAccessRequestCreation(false);
        return;
      case "vehicles":
        setVehicleCreation(false);
        return;
      case "rules":
        setRuleCreation(false);
        return;
    }
  }, [selectedRoute]);

  type EnvType = "production" | "staging" | "demo" | "development";
  const env = import.meta.env["VITE_TARGET_ENV"] as EnvType;

  if (loadAllCompaniesStatus === "success" && activeCompanies.length === 0) {
    return <AccessErrorPage pageType="noCompaniesAccess" />;
  }

  const props = {
    sections: [
      {
        title: t("home.menu.section.home.title"),
        routes: [
          {
            route: "",
            title: t("home.menu.home"),
          },
        ],
      },
      {
        title: t("home.menu.section.expenses.title"),
        routes: [
          {
            route: "transactions",
            title: t("home.menu.transactions"),
          },
          {
            route: "cards",
            newCreation: cardCreation || badgeCreation,
            title: t("home.menu.cards"),
          },
          {
            route: "billings",
            title: t("home.menu.billings"),
          },
        ],
      },
      {
        title: t("home.menu.section.fleet.title"),
        routes: [
          {
            route: "drivers",
            newCreation: driverAccessRequestCreation,
            title: t("home.menu.drivers"),
          },
          {
            route: "vehicles",
            newCreation: vehicleCreation,
            title: t("home.menu.vehicles"),
          },
          {
            route: "fleet",
            title: t("home.menu.fleet"),
          },
          {
            route: "electrification",
            title: t("home.menu.electrification"),
          },
          {
            route: "geolocation",
            title: t("home.menu.geolocation"),
          },
        ],
      },
      {
        title: t("home.menu.section.configuration.title"),
        routes: [
          {
            route: "rules",
            newCreation: ruleCreation,
            title: t("home.menu.rules"),
          },
          {
            route: "users/management",
            newCreation: ruleCreation,
            title: t("home.menu.userManagement"),
          },
          {
            route: "notifications",
            title: t("home.menu.notifications"),
          },
        ],
      },
    ],
    route: "users",
    selectedRoute,
    onChangeRoute,
    logout,
    companies: activeCompanies,
    currentCompany: company?.name ?? "",
    onChangeCompany,
    onClickCompanyCreation,
    hasAnalyticsAccess: company?.hasAnalytics ?? false,
    hasAdvancedAnalyticsAccess: company?.hasAdvancedAnalytics ?? false,
    canOrderBadges: company?.canOrderBadges ?? false,
    isInProduction: env === "production",
    isInDemo: env === "demo",
    canCreateCompany: manager?.role === "OWNER" || false,
    hasGeolocationAccess: company?.hasGeolocation ?? false,
  };

  const companySelectorProps = {
    canCreateCompany: manager?.role === "OWNER" || false,
    companies: activeCompanies,
    onClickCompanyCreation,
    currentCompany: company?.name ?? "",
    onChangeCompany,
    size: "L" as const,
  };

  return (
    <StyledContainer>
      <StyledSideBar $fullScreen={fullScreen}>
        <SideBar {...props}>
          <CompanySelector {...companySelectorProps}></CompanySelector>
        </SideBar>
      </StyledSideBar>
      <Outlet context={{ setFullScreen }} />
    </StyledContainer>
  );
};

const StyledSideBar = styled.div<{ $fullScreen: boolean }>`
  width: ${({ $fullScreen }) =>
    $fullScreen
      ? "0rem"
      : "17.125rem"}; /*because side bar menu si always open*/
`;

const StyledContainer = styled.div`
  display: flex;
  height: 100%;
  background-color: #273c35;
`;
